import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { useUserEdit, UserEditInput } from "@/graphql/hooks/useUserEdit"
import { Switch } from "@/components/ui/switch"
import { Calendar, CreditCard, User } from "lucide-react"
import { validateRUT } from "@/lib/rutValidation"

const documents = new Map([
  [0, "none"],
  [1, "RUT"],
  [2, "CC"],
  [3, "CI"],
  [4, "DNI"],
])

const userEditSchema = z
  .object({
    id: z.number().min(1, "ID es requerido"),
    name: z.string().min(1, "El nombre es requerido"),
    email: z.string().email("Email inválido"),
    firstSurname: z.string().min(1, "El primer apellido es requerido"),
    lastSurname: z.string().optional(),
    birthday: z.string().optional(),
    genderId: z.number().min(1, "El género es requerido"),
    categoryId: z.number().min(1, "La categoría es requerida"),
    active: z.number(),
    documentNumberType: z.string().optional(),
    documentNumber: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.documentNumberType === "none") {
        return true
      }

      if (data.documentNumberType === "RUT") {
        return validateRUT(data.documentNumber)
      }
      // Para otros tipos, validar que no tenga espacios ni caracteres especiales
      return /^[a-zA-Z0-9]+$/.test(data.documentNumber)
    },
    {
      message: "Número de documento inválido",
      path: ["documentNumber"],
    },
  )

type UserEditFormData = z.infer<typeof userEditSchema>

interface UserEditModalProps {
  user
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

// Static data for genders and categories (these would normally come from an API)
const genders = [
  { id: 1, name: "Masculino" },
  { id: 2, name: "Femenino" },
  { id: 3, name: "Otro" },
]

const categories = [
  { id: 1, name: "Casual" },
  { id: 2, name: "Social" },
  { id: 3, name: "Competitivo" },
  { id: 4, name: "Aprendiz" },
]

export function UserEditModal({ user, open, onOpenChange, onSuccess }: UserEditModalProps) {
  const { updateUser, loading } = useUserEdit()

  const form = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      id: 1,
      name: "",
      firstSurname: "",
      lastSurname: "",
      email: "",
      birthday: "",
      genderId: 1,
      categoryId: 1,
      active: 1,
      documentNumberType: "RUT",
      documentNumber: "",
    },
  })

  // Populate form when user changes
  useEffect(() => {
    if (user && open) {
      form.reset({
        id: user.id,
        name: user.name || "",
        firstSurname: user.firstSurname || "",
        lastSurname: user.lastSurname || "",
        email: user.email || "",
        birthday: user.birthday || "",
        genderId: user.genderId || 1,
        categoryId: user.categoryId || 1,
        active: user.active === 2 ? 0 : 1,
        documentNumberType: documents.get(user.documentNumberType) || "RUT",
        documentNumber: user.documentNumber || "",
      })
    }
  }, [user, open, form])

  const onSubmit = async (data: UserEditFormData) => {
    try {
      // Convert form data to UserEditInput format - only send the allowed fields
      const updateData: UserEditInput = {
        id: data.id,
        name: data.name,
        firstSurname: data.firstSurname,
        lastSurname: data.lastSurname,
        email: data.email,
        birthday: data.birthday,
        genderId: data.genderId,
        categoryId: data.categoryId,
        active: data.active,
        documentNumberType: data.documentNumberType,
        documentNumber: data.documentNumber,
      }

      const resolve = await updateUser(updateData)
      if (resolve.data?.UserEdit?.status?.success) {
        onOpenChange(false)
        onSuccess?.()
      }
    } catch (error) {
      console.error("Error updating user:", error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Editar Usuario - {user?.fullName}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-md font-medium">
                <User className="w-4 h-4" />
                Información Personal
              </div>
              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre *</FormLabel>
                      <FormControl>
                        <Input placeholder="Nombre" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="firstSurname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primer Apellido *</FormLabel>
                      <FormControl>
                        <Input placeholder="Primer apellido" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastSurname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Segundo Apellido</FormLabel>
                      <FormControl>
                        <Input placeholder="Segundo apellido" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="email@ejemplo.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="genderId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Género *</FormLabel>
                      <Select
                        value={field.value?.toString()}
                        onValueChange={(value) => field.onChange(parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar género" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {genders.map((gender) => (
                            <SelectItem key={gender.id} value={gender.id.toString()}>
                              {gender.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoría *</FormLabel>
                      <Select
                        value={field.value?.toString()}
                        onValueChange={(value) => field.onChange(parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar categoría" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="birthday"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Fecha de Nacimiento
                      </FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                      <div className="space-y-0.5">
                        <FormLabel>Usuario Activo</FormLabel>
                        <div className="text-sm text-muted-foreground">
                          Permite al usuario acceder al sistema
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value === 1}
                          onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Documento de Identidad */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-md font-medium">
                <CreditCard className="w-4 h-4" />
                Documento de Identidad
              </div>
              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="documentNumberType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de documento *</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona tipo de documento" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Sin Número de documento</SelectItem>
                          <SelectItem value="RUT">RUT (Chile)</SelectItem>
                          <SelectItem value="CC">Cédula de Ciudadanía (CC)</SelectItem>
                          <SelectItem value="CI">Cédula de Identidad (CI)</SelectItem>
                          <SelectItem value="DNI">DNI</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="documentNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número de documento *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            form.watch("documentNumberType") === "RUT"
                              ? "12345678-9"
                              : "Número de documento"
                          }
                          {...field}
                        />
                      </FormControl>
                      {form.watch("documentNumberType") === "RUT" && (
                        <p className="text-xs text-muted-foreground">
                          Formato: 12345678-9 (con o sin puntos y guión)
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
